/**
 * FeedbackPage Component
 *
 * User-facing page showing their submitted feedback requests.
 * Admins see tabs to switch between their feedback and admin dashboard.
 */

import { useState } from 'react';
import { MessageSquarePlus, Bug, Lightbulb, HelpCircle, Clock, RefreshCw, Shield, Filter, ChevronDown, User, CheckCircle2, Circle, Loader2, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FeedbackModal } from './FeedbackModal';
import { CodexBriefButton } from './CodexBriefButton';
import { useMyFeedback, useFeedbackList, useFeedbackMetrics } from '@/hooks/useFeedback';
import { updateFeedbackStatus, resolveFeedback } from '@/services/feedbackService';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { toast } from 'sonner';
import {
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_STATUS_COLORS,
  FEEDBACK_TYPE_LABELS,
  FEEDBACK_TYPE_COLORS,
  type FeedbackRequest,
  type FeedbackRequestWithDetails,
  type FeedbackType,
  type FeedbackStatus,
  type FeedbackFilters
} from '@/types/feedback';
import { formatDistanceToNow } from 'date-fns';

const ALL_STATUSES: readonly FeedbackStatus[] = [
  'submitted', 'triaged', 'planned', 'in_progress',
  'resolved', 'closed', 'wont_fix', 'duplicate'
] as const;
const OPEN_STATUSES: FeedbackStatus[] = ['submitted', 'triaged', 'planned', 'in_progress'];

const TYPE_ICONS: Record<FeedbackType, React.ReactNode> = {
  bug: <Bug className="h-4 w-4" />,
  enhancement: <Lightbulb className="h-4 w-4" />,
  question: <HelpCircle className="h-4 w-4" />
};

export function FeedbackPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'my-feedback' | 'admin'>('my-feedback');
  const { isAdmin } = useRoleAccess();

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Feedback</h1>
          <p className="text-muted-foreground">
            {isAdmin ? 'Manage feedback and track submissions' : 'Track your submitted bug reports and feature requests'}
          </p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <MessageSquarePlus className="h-4 w-4 mr-2" />
          New Feedback
        </Button>
      </div>

      {/* Admin gets tabs, regular users see their feedback directly */}
      {isAdmin ? (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'my-feedback' | 'admin')}>
          <TabsList>
            <TabsTrigger value="my-feedback" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              My Feedback
            </TabsTrigger>
            <TabsTrigger value="admin" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Admin Dashboard
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-feedback" className="mt-6">
            <MyFeedbackContent onOpenModal={() => setIsModalOpen(true)} />
          </TabsContent>

          <TabsContent value="admin" className="mt-6">
            <AdminDashboard />
          </TabsContent>
        </Tabs>
      ) : (
        <MyFeedbackContent onOpenModal={() => setIsModalOpen(true)} />
      )}

      {/* Modal */}
      <FeedbackModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  );
}

// =====================================================
// My Feedback Content (for regular users and admin's "My Feedback" tab)
// =====================================================

function MyFeedbackContent({ onOpenModal }: { onOpenModal: () => void }) {
  const { data, count, loading, error, refetch } = useMyFeedback();

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          title="Total Submitted"
          value={count}
          icon={<MessageSquarePlus className="h-4 w-4" />}
          loading={loading}
        />
        <SummaryCard
          title="Bug Reports"
          value={data.filter(f => f.type === 'bug').length}
          icon={<Bug className="h-4 w-4" />}
          loading={loading}
        />
        <SummaryCard
          title="Enhancements"
          value={data.filter(f => f.type === 'enhancement').length}
          icon={<Lightbulb className="h-4 w-4" />}
          loading={loading}
        />
      </div>

      {/* Feedback Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Submitted Feedback</CardTitle>
            <CardDescription>
              Your feedback history and current status
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={refetch} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-8 text-destructive">
              <p>Failed to load feedback: {error}</p>
              <Button variant="link" onClick={refetch}>Try again</Button>
            </div>
          ) : loading ? (
            <FeedbackTableSkeleton />
          ) : data.length === 0 ? (
            <EmptyState onSubmit={onOpenModal} />
          ) : (
            <FeedbackTable data={data} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =====================================================
// Admin Dashboard
// =====================================================

function AdminDashboard() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const { data: metrics, loading: metricsLoading } = useFeedbackMetrics();
  const {
    data,
    count,
    loading,
    error,
    refetch,
    setFilters,
    page,
    setPage,
    pageSize
  } = useFeedbackList();

  const handleFilterChange = (status: string, type: string) => {
    const filters: FeedbackFilters = {};
    if (status !== 'all') {
      filters.status = [status as FeedbackStatus];
    }
    if (type !== 'all') {
      filters.type = [type as FeedbackType];
    }
    setFilters(filters);
  };

  // Resolution-notes dialog state.
  const [resolveTarget, setResolveTarget] = useState<string | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveSubmitting, setResolveSubmitting] = useState(false);

  const handleStatusChange = async (feedbackId: string, newStatus: FeedbackStatus) => {
    if (newStatus === 'resolved') {
      setResolveTarget(feedbackId);
      setResolveNotes('');
      return;
    }
    try {
      await updateFeedbackStatus(feedbackId, newStatus);
      toast.success(`Status updated to ${FEEDBACK_STATUS_LABELS[newStatus]}`);
      refetch();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleConfirmResolve = async () => {
    if (!resolveTarget) {
      return;
    }
    const notes = resolveNotes.trim();
    if (!notes) {
      toast.error('Resolution notes are required');
      return;
    }
    setResolveSubmitting(true);
    try {
      const result = await resolveFeedback(resolveTarget, notes);
      if (result?.emailFailed) {
        toast.warning('Resolved, but email failed — click resolve again to retry.');
      } else if (result?.stampWarning === 'idempotency_stamp_failed') {
        toast.warning('Email sent, but idempotency stamp failed. Do not retry until checked.');
      } else if (result?.skipped === 'no_op') {
        toast.info('Already resolved and emailed.');
      } else if (result?.skipped === 'already_emailed') {
        toast.success('Resolved (email was already sent previously).');
      } else {
        toast.success('Resolved and submitter notified.');
      }
      setResolveTarget(null);
      setResolveNotes('');
      refetch();
    } catch {
      toast.error('Failed to resolve feedback');
    } finally {
      setResolveSubmitting(false);
    }
  };

  const totalPages = Math.ceil(count / pageSize);
  const openCount = OPEN_STATUSES.reduce(
    (sum, k) => sum + (metrics?.by_status?.[k] ?? 0),
    0
  );
  const bugCount = metrics?.by_type?.bug ?? 0;

  return (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard
          title="Total"
          value={metrics?.total || 0}
          icon={<MessageSquarePlus className="h-4 w-4" />}
          loading={metricsLoading}
        />
        <SummaryCard
          title="Open"
          value={openCount}
          icon={<Circle className="h-4 w-4" />}
          loading={metricsLoading}
          highlight={openCount > 0 ? 'warning' : undefined}
        />
        <SummaryCard
          title="In Progress"
          value={metrics?.by_status?.in_progress || 0}
          icon={<Loader2 className="h-4 w-4" />}
          loading={metricsLoading}
        />
        <SummaryCard
          title="Resolved"
          value={metrics?.by_status?.resolved || 0}
          icon={<CheckCircle2 className="h-4 w-4" />}
          loading={metricsLoading}
        />
        <SummaryCard
          title="Bugs"
          value={bugCount}
          icon={<Bug className="h-4 w-4" />}
          loading={metricsLoading}
          highlight={bugCount > 0 ? 'danger' : undefined}
        />
      </div>

      {/* Filters & Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>All Feedback</CardTitle>
            <CardDescription>
              {count} total submissions
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                handleFilterChange(v, typeFilter);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {ALL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{FEEDBACK_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={typeFilter}
              onValueChange={(v) => {
                setTypeFilter(v);
                handleFilterChange(statusFilter, v);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="bug">Bug</SelectItem>
                <SelectItem value="enhancement">Enhancement</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={refetch} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-8 text-destructive">
              <p>Failed to load feedback: {error}</p>
              <Button variant="link" onClick={refetch}>Try again</Button>
            </div>
          ) : loading ? (
            <FeedbackTableSkeleton />
          ) : data.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquarePlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No feedback matches your filters</p>
            </div>
          ) : (
            <>
              <AdminFeedbackTable data={data} onStatusChange={handleStatusChange} />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page <= 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Resolution-notes dialog: shown when admin sets status -> resolved */}
      <Dialog
        open={resolveTarget !== null}
        onOpenChange={(o) => {
          if (!o && !resolveSubmitting) {
            setResolveTarget(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Resolve feedback</DialogTitle>
            <DialogDescription>
              These notes are included in the email sent to the submitter.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="What was done? Reference a commit, link, or summary…"
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
            rows={5}
            disabled={resolveSubmitting}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResolveTarget(null)}
              disabled={resolveSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmResolve}
              disabled={resolveSubmitting || !resolveNotes.trim()}
            >
              {resolveSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resolve & email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =====================================================
// Admin Feedback Table (with status dropdown)
// =====================================================

interface AdminFeedbackTableProps {
  data: FeedbackRequestWithDetails[];
  onStatusChange: (id: string, status: FeedbackStatus) => void;
}

function AdminFeedbackTable({ data, onStatusChange }: AdminFeedbackTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[100px]">Ticket</TableHead>
          <TableHead>Title</TableHead>
          <TableHead className="w-[80px]">Files</TableHead>
          <TableHead className="w-[100px]">Type</TableHead>
          <TableHead className="w-[100px]">Severity</TableHead>
          <TableHead className="w-[140px]">Status</TableHead>
          <TableHead className="w-[120px]">Submitted By</TableHead>
          <TableHead className="w-[120px]">Date</TableHead>
          <TableHead className="w-[140px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((feedback) => (
          <TableRow key={feedback.id}>
            <TableCell className="font-mono text-sm">
              {feedback.ticket_id}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                {TYPE_ICONS[feedback.type]}
                <span className="font-medium truncate max-w-[250px]" title={feedback.title}>
                  {feedback.title}
                </span>
              </div>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {Array.isArray(feedback.attachments) && feedback.attachments.length > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />
                  {feedback.attachments.length}
                </span>
              ) : (
                <span className="text-muted-foreground/50">—</span>
              )}
            </TableCell>
            <TableCell>
              <Badge variant="outline" className={FEEDBACK_TYPE_COLORS[feedback.type]}>
                {FEEDBACK_TYPE_LABELS[feedback.type]}
              </Badge>
            </TableCell>
            <TableCell>
              {feedback.severity && (
                <Badge
                  variant="outline"
                  className={
                    feedback.severity === 'critical' ? 'border-red-500 text-red-600' :
                    feedback.severity === 'major' ? 'border-orange-500 text-orange-600' :
                    feedback.severity === 'minor' ? 'border-yellow-500 text-yellow-600' :
                    'border-gray-400 text-gray-500'
                  }
                >
                  {feedback.severity}
                </Badge>
              )}
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className={`w-full justify-between ${FEEDBACK_STATUS_COLORS[feedback.status]}`}>
                    {FEEDBACK_STATUS_LABELS[feedback.status]}
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {ALL_STATUSES.map((status) => (
                    <DropdownMenuItem
                      key={status}
                      onClick={() => onStatusChange(feedback.id, status)}
                      className={feedback.status === status ? 'bg-accent' : ''}
                    >
                      {FEEDBACK_STATUS_LABELS[status]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {/* Note: User join not available, showing truncated UUID */}
              {feedback.submitted_by?.slice(0, 8) || 'Unknown'}...
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatDistanceToNow(new Date(feedback.created_at), { addSuffix: true })}
            </TableCell>
            <TableCell>
              <CodexBriefButton feedbackId={feedback.id} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// =====================================================
// Sub-components
// =====================================================

interface SummaryCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  loading?: boolean;
  highlight?: 'warning' | 'danger';
}

function SummaryCard({ title, value, icon, loading, highlight }: SummaryCardProps) {
  return (
    <Card className={
      highlight === 'warning' ? 'border-yellow-500/50' :
      highlight === 'danger' ? 'border-red-500/50' :
      ''
    }>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            {loading ? (
              <Skeleton className="h-8 w-12 mt-1" />
            ) : (
              <p className={`text-2xl font-bold ${
                highlight === 'warning' ? 'text-yellow-600' :
                highlight === 'danger' ? 'text-red-600' :
                ''
              }`}>{value}</p>
            )}
          </div>
          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
            highlight === 'warning' ? 'bg-yellow-100 text-yellow-600' :
            highlight === 'danger' ? 'bg-red-100 text-red-600' :
            'bg-muted'
          }`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface FeedbackTableProps {
  data: FeedbackRequest[];
}

function FeedbackTable({ data }: FeedbackTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[100px]">Ticket ID</TableHead>
          <TableHead>Title</TableHead>
          <TableHead className="w-[100px]">Type</TableHead>
          <TableHead className="w-[120px]">Status</TableHead>
          <TableHead className="w-[150px]">Submitted</TableHead>
          <TableHead className="w-[150px]">Last Updated</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((feedback) => (
          <TableRow key={feedback.id}>
            <TableCell className="font-mono text-sm">
              {feedback.ticket_id}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                {TYPE_ICONS[feedback.type]}
                <span className="font-medium truncate max-w-[300px]">
                  {feedback.title}
                </span>
              </div>
            </TableCell>
            <TableCell>
              <Badge variant="outline" className={FEEDBACK_TYPE_COLORS[feedback.type]}>
                {FEEDBACK_TYPE_LABELS[feedback.type]}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant="outline" className={FEEDBACK_STATUS_COLORS[feedback.status]}>
                {FEEDBACK_STATUS_LABELS[feedback.status]}
              </Badge>
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDistanceToNow(new Date(feedback.created_at), { addSuffix: true })}
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatDistanceToNow(new Date(feedback.updated_at), { addSuffix: true })}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function FeedbackTableSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

interface EmptyStateProps {
  onSubmit: () => void;
}

function EmptyState({ onSubmit }: EmptyStateProps) {
  return (
    <div className="text-center py-12">
      <MessageSquarePlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium mb-2">No feedback submitted yet</h3>
      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
        Help us improve Lendwise by reporting bugs, suggesting new features, or asking questions.
      </p>
      <Button onClick={onSubmit}>
        <MessageSquarePlus className="h-4 w-4 mr-2" />
        Submit Your First Feedback
      </Button>
    </div>
  );
}
